import { useEffect, useRef } from 'react';
import './WaterfallAnimation.css';

export default function WaterfallAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for both Pokemon
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate centers
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderCenterX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderCenterY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];
    
    // PHASE 1: WATERFALL PREPARATION (0-500ms)
    // Initial attacker animation - waterfall summoning
    attackerEl.style.transition = 'transform 0.5s ease-in-out, filter 0.5s ease-in-out';
    attackerEl.style.transform = 'scale(1.15)';
    attackerEl.style.filter = 'brightness(1.4) drop-shadow(0 0 20px #006994)';
    
    // Create water summoning aura around attacker
    const summoningAura = document.createElement('div');
    summoningAura.className = 'waterfall-summoning-aura';
    summoningAura.style.left = `${attackerCenterX}px`;
    summoningAura.style.top = `${attackerCenterY}px`;
    container.appendChild(summoningAura);
    elements.push(summoningAura);
    
    // Create water energy particles spiraling upward around attacker
    for (let i = 0; i < 20; i++) {
      const energyTimer = setTimeout(() => {
        const particle = document.createElement('div');
        particle.className = 'waterfall-energy-particle';
        
        const particleAngle = (i / 20) * Math.PI * 2;
        const particleDistance = 40 + Math.random() * 25;
        particle.style.left = `${attackerCenterX + Math.cos(particleAngle) * particleDistance}px`;
        particle.style.top = `${attackerCenterY + Math.sin(particleAngle) * particleDistance}px`;
        particle.style.setProperty('--particle-delay', `${i * 0.025}s`);
        particle.style.setProperty('--spiral-height', `${-60 - Math.random() * 40}px`);
        
        container.appendChild(particle);
        elements.push(particle);
        
        // Auto-remove particle
        const particleRemoveTimer = setTimeout(() => {
          if (particle.isConnected) {
            particle.remove();
          }
        }, 800);
        timers.push(particleRemoveTimer);
      }, i * 25);
      timers.push(energyTimer);
    }

    // PHASE 2: WATERFALL SOURCE CREATION (500-800ms)
    const sourceTimer = setTimeout(() => {
      // Create waterfall source high above opponent
      const waterfallSource = document.createElement('div');
      waterfallSource.className = 'waterfall-source';
      waterfallSource.style.left = `${defenderCenterX}px`;
      waterfallSource.style.top = `${defenderCenterY - 200}px`; // High above opponent
      
      container.appendChild(waterfallSource);
      elements.push(waterfallSource);
      
      // Create water gathering effect at source
      const sourceGather = document.createElement('div');
      sourceGather.className = 'waterfall-source-gather';
      sourceGather.style.left = `${defenderCenterX}px`;
      sourceGather.style.top = `${defenderCenterY - 200}px`;
      
      container.appendChild(sourceGather);
      elements.push(sourceGather);
      
      // Create pre-cascade water droplets
      for (let i = 0; i < 15; i++) {
        const dropTimer = setTimeout(() => {
          const droplet = document.createElement('div');
          droplet.className = 'waterfall-pre-droplet';
          
          const dropX = defenderCenterX + (Math.random() - 0.5) * 60;
          const dropY = defenderCenterY - 180 + Math.random() * 30;
          droplet.style.left = `${dropX}px`;
          droplet.style.top = `${dropY}px`;
          droplet.style.setProperty('--fall-distance', `${180 + Math.random() * 40}px`);
          droplet.style.setProperty('--fall-delay', `${i * 0.03}s`);
          
          container.appendChild(droplet);
          elements.push(droplet);
          
          const dropletRemoveTimer = setTimeout(() => {
            if (droplet.parentNode) {
              droplet.remove();
            }
          }, 1000);
          timers.push(dropletRemoveTimer);
        }, i * 20);
        timers.push(dropTimer);
      }
    }, 500);
    timers.push(sourceTimer);
        
    // PHASE 3: MASSIVE WATERFALL CASCADE (800-1600ms)
    const cascadeTimer = setTimeout(() => {
      // Create main waterfall cascade
      const mainCascade = document.createElement('div');
      mainCascade.className = 'waterfall-main-cascade';
      mainCascade.style.left = `${defenderCenterX}px`;
      mainCascade.style.top = `${defenderCenterY - 200}px`;
      mainCascade.style.setProperty('--cascade-height', '220px');
      
      container.appendChild(mainCascade);
      elements.push(mainCascade);
      
      // Create multiple water streams within the cascade
      for (let i = 0; i < 8; i++) {
        const streamTimer = setTimeout(() => {
          const stream = document.createElement('div');
          stream.className = 'waterfall-water-stream';
          
          const streamX = defenderCenterX + (Math.random() - 0.5) * 40;
          stream.style.left = `${streamX}px`;
          stream.style.top = `${defenderCenterY - 200}px`;
          stream.style.setProperty('--stream-width', `${8 + Math.random() * 12}px`);
          stream.style.setProperty('--stream-delay', `${i * 0.05}s`);
          stream.style.setProperty('--stream-height', `${200 + Math.random() * 30}px`);
          
          container.appendChild(stream);
          elements.push(stream);
        }, i * 50);
        timers.push(streamTimer);
      }
      
      // Create waterfall mist effects
      for (let i = 0; i < 12; i++) {
        const mistTimer = setTimeout(() => {
          const mist = document.createElement('div');
          mist.className = 'waterfall-mist';
          
          const mistX = defenderCenterX + (Math.random() - 0.5) * 80;
          const mistY = defenderCenterY - 100 + Math.random() * 80;
          mist.style.left = `${mistX}px`;
          mist.style.top = `${mistY}px`;
          mist.style.setProperty('--mist-drift-x', `${(Math.random() - 0.5) * 40}px`);
          mist.style.setProperty('--mist-drift-y', `${-20 - Math.random() * 30}px`);
          
          container.appendChild(mist);
          elements.push(mist);
          
          const mistRemoveTimer = setTimeout(() => {
            if (mist.parentNode) {
              mist.remove();
            }
          }, 1200);
          timers.push(mistRemoveTimer);
        }, i * 60);
        timers.push(mistTimer);
      }
      
      // Create impact at opponent location
      const impactTimer = setTimeout(() => {
        // Make defender react with waterfall hit animation
        defenderEl.classList.add('waterfall-hit');
        defenderEl.style.filter = 'brightness(1.5) hue-rotate(200deg) saturate(1.6)';
        defenderEl.style.transform = 'translateY(5px) scale(0.9)';
        
        // Create massive water impact splash
        const impact = document.createElement('div');
        impact.className = 'waterfall-impact-splash';
        impact.style.left = `${defenderCenterX}px`;
        impact.style.top = `${defenderCenterY + 20}px`;
        
        container.appendChild(impact);
        elements.push(impact);
        
        // Create water pooling effect
        const pooling = document.createElement('div');
        pooling.className = 'waterfall-water-pooling';
        pooling.style.left = `${defenderCenterX}px`;
        pooling.style.top = `${defenderCenterY + 40}px`;
        
        container.appendChild(pooling);
        elements.push(pooling);
        
        // Create splash particles in all directions
        for (let i = 0; i < 30; i++) {
          const splashParticle = document.createElement('div');
          splashParticle.className = 'waterfall-splash-particle';
          
          const splashAngle = Math.random() * Math.PI * 2;
          const splashDistance = 25 + Math.random() * 60;
          splashParticle.style.left = `${defenderCenterX + Math.cos(splashAngle) * splashDistance}px`;
          splashParticle.style.top = `${defenderCenterY + Math.sin(splashAngle) * splashDistance}px`;
          
          splashParticle.style.setProperty('--splash-velocity-x', `${(Math.random() - 0.5) * 100}px`);
          splashParticle.style.setProperty('--splash-velocity-y', `${-50 - Math.random() * 50}px`);
          splashParticle.style.setProperty('--splash-size', `${3 + Math.random() * 6}px`);
          
          container.appendChild(splashParticle);
          elements.push(splashParticle);
          
          const splashRemoveTimer = setTimeout(() => {
            if (splashParticle.parentNode) {
              splashParticle.remove();
            }
          }, 1200);
          timers.push(splashRemoveTimer);
        }
        
        // Reset defender after hit effect
        const defenderResetTimer = setTimeout(() => {
          defenderEl.classList.remove('waterfall-hit');
          defenderEl.style.filter = '';
          defenderEl.style.transform = '';
        }, 700);
        timers.push(defenderResetTimer);
        
      }, 400); // Impact timing after cascade starts
      timers.push(impactTimer);
      
    }, 800); // Cascade timing
    timers.push(cascadeTimer);
    
    // PHASE 4: RESET ATTACKER (1600-2000ms)
    const resetTimer = setTimeout(() => {
      // Reset attacker to normal state
      attackerEl.style.transition = 'transform 0.4s ease-out, filter 0.4s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 1600);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2000);
    timers.push(completeTimer);
    
    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('waterfall-hit');
        defenderEl.style.filter = '';
        defenderEl.style.transform = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining effects
      const remainingElements = container.querySelectorAll(
        '.waterfall-summoning-aura, .waterfall-energy-particle, .waterfall-source, ' +
        '.waterfall-source-gather, .waterfall-pre-droplet, .waterfall-main-cascade, ' +
        '.waterfall-water-stream, .waterfall-mist, .waterfall-impact-splash, ' +
        '.waterfall-water-pooling, .waterfall-splash-particle'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);
  
  return null;
}

WaterfallAnimation.moveType = "water";
